import React, { useState } from "react";
import { Trash2, Loader2, RefreshCw } from "lucide-react";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";

const SCOPES = [
  { key: "search", label: "Search results", hint: "Saved semantic search matches" },
  { key: "venues", label: "Venue names", hint: "Geocoded pin/venue names" },
  { key: "translations", label: "Deal translations", hint: "Cached per-deal translations" },
  { key: "fx", label: "Currency rates", hint: "Force a fresh live FX pull" },
];

// One place to clear the app's derived caches. Admins pick which ones (or all) so they can
// refresh just what looks stale. Deals and accounts are never touched.
export const AdminCachePurge = () => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const [picked, setPicked] = useState({ search: true, venues: true, translations: true, fx: true });

  const toggle = (k) => setPicked((p) => ({ ...p, [k]: !p[k] }));
  const chosen = SCOPES.filter((s) => picked[s.key]).map((s) => s.key);
  const allOn = chosen.length === SCOPES.length;

  const purge = async () => {
    if (chosen.length === 0) return;
    setBusy(true);
    try {
      // When everything is picked, send no scopes so the server clears all.
      const body = allOn ? {} : { scopes: chosen };
      const { data } = await api.post("/admin/cache/purge", body);
      setLast(data.cleared || {});
      toast({ title: "Cache purged", description: `Cleared: ${(data.scopes || []).join(", ")}.` });
    } catch (e) {
      toast({ title: "Could not purge", description: e.response?.data?.detail || "Try again." });
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5 space-y-4" data-testid="admin-cache-purge">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div>
          <h3 className="font-bold text-foreground flex items-center gap-2">
            <RefreshCw size={18} className="text-[#16a34a]" /> Purge cache
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Clears the app's derived caches so the next request rebuilds them fresh. Pick which ones
            to clear, or leave all ticked. Deals and accounts are untouched.
          </p>
        </div>
        <button onClick={purge} disabled={busy || chosen.length === 0} data-testid="cache-purge-button"
          className="shrink-0 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-white bg-rose-600 hover:bg-rose-700 transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
          {allOn ? "Purge all caches" : `Purge ${chosen.length} selected`}
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-2">
        {SCOPES.map((s) => (
          <label key={s.key} data-testid={`cache-scope-${s.key}`}
            className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${picked[s.key] ? "border-[#16a34a]/50 bg-[#16a34a]/5" : "border-border hover:bg-secondary/40"}`}>
            <input type="checkbox" checked={!!picked[s.key]} onChange={() => toggle(s.key)}
              data-testid={`cache-scope-${s.key}-input`}
              className="mt-0.5 h-4 w-4 accent-[#16a34a]" />
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-foreground">{s.label}</span>
              <span className="block text-xs text-muted-foreground">{s.hint}</span>
            </span>
          </label>
        ))}
      </div>

      {last && (
        <div className="rounded-xl border border-border bg-background px-3 py-2.5 text-xs font-semibold text-muted-foreground flex flex-wrap gap-x-5 gap-y-1" data-testid="cache-purge-result">
          {last.search != null && <span>Search: <span className="text-foreground">{last.search}</span></span>}
          {last.venues != null && <span>Venues: <span className="text-foreground">{last.venues}</span></span>}
          {last.translations != null && <span>Translations: <span className="text-foreground">{last.translations}</span></span>}
          {last.fxSource != null && <span>FX: <span className="text-foreground">{last.fxSource}</span></span>}
        </div>
      )}
    </div>
  );
};

export default AdminCachePurge;
